<template>
  <div class="cost-management-container">
    <!-- Header -->
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <span class="cost-management-title">Gestión de Costos y Precios</span>
        <div class="d-flex flex-column flex-md-row ga-2 ga-md-4 align-center">
          <v-btn
            color="success"
            prepend-icon="mdi-calculator"
            @click="calculateABCCategories"
            :loading="calculating"
            size="default"
          >
            <span class="d-none d-sm-inline">Calcular Categorías ABC</span>
          </v-btn>
          <v-btn
            color="primary"
            prepend-icon="mdi-microsoft-excel"
            @click="exportCosts"
            :loading="exporting"
            size="default"
          >
            <span class="d-none d-sm-inline">Exportar</span>
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Selector de Período -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-card variant="outlined" class="pa-4">
              <v-card-title class="text-h6 pb-2">Período de Análisis</v-card-title>
              <v-select
                v-model="selectedPeriod"
                :items="periodOptions"
                label="Seleccionar período"
                item-title="title"
                item-value="value"
                density="compact"
                @update:model-value="calculatePeriodStatistics"
                :loading="calculatingPeriod"
              ></v-select>
              <div v-if="periodInfo" class="text-caption text-grey mt-2">
                <v-icon size="small" class="mr-1">mdi-calendar</v-icon>
                {{ formatPeriodLabel(periodInfo) }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="8">
            <v-card variant="outlined" class="pa-4">
              <v-card-title class="text-h6 pb-2">Resumen del Período</v-card-title>
              <v-row v-if="periodSummary">
                <v-col cols="4">
                  <div class="text-center">
                    <div class="text-h5 text-primary">{{ periodSummary.total_products }}</div>
                    <div class="text-caption">Productos con Ventas</div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-center">
                    <div class="text-h5 text-success">{{ periodSummary.total_orders }}</div>
                    <div class="text-caption">Órdenes Enviadas</div>
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="text-center">
                    <div class="text-h5 text-info">{{ formatCurrency(periodSummary.total_revenue) }}</div>
                    <div class="text-caption">Ingresos Totales</div>
                  </div>
                </v-col>
              </v-row>
              <div v-else class="text-center text-grey">
                Selecciona un período para ver el resumen
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filtros -->
        <v-row class="mb-4 cost-management-filters">
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar producto"
              hide-details
              density="compact"
              style="max-width: 100%;"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.category"
              :items="categoryOptions"
              label="Categoría"
              hide-details
              density="compact"
              clearable
              style="max-width: 100%;"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.abcCategory"
              :items="[
                { title: 'Categoría A', value: 'A' },
                { title: 'Categoría B', value: 'B' },
                { title: 'Categoría C', value: 'C' },
                { title: 'Sin categoría', value: 'null' }
              ]"
              label="Categoría ABC"
              hide-details
              density="compact"
              clearable
              style="max-width: 100%;"
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.status"
              :items="[
                { title: 'Activo', value: 'active' },
                { title: 'Descontinuado', value: 'discontinued' },
                { title: 'Bajo stock', value: 'low_stock' },
                { title: 'Sin stock', value: 'out_of_stock' }
              ]"
              label="Estado"
              hide-details
              density="compact"
              clearable
              style="max-width: 100%;"
            ></v-select>
          </v-col>
        </v-row>

        <!-- Tabla de productos -->
        <ResponsiveDataTable
          :items="filteredProducts"
          :headers="headers"
          :loading="loading"
          :search="filters.search"
          class="elevation-1"
          :show-select="false"
          items-per-page="15"
          density="default"
          hover
        >
          <template v-slot:item.abc_category="{ item }">
            <v-chip
              v-if="item.abc_category"
              :color="getABCColor(item.abc_category)"
              size="small"
            >
              {{ item.abc_category }}
            </v-chip>
            <span v-else class="text-grey">Sin categoría</span>
          </template>

          <template v-slot:item.unit_price="{ item }">
            <v-text-field
              v-model="item.unit_price_display"
              type="text"
              density="comfortable"
              hide-details
              @blur="updateProductUnitPrice(item)"
              @keyup.enter="updateProductUnitPrice(item)"
              @input="formatTablePriceInput(item, $event)"
              prefix="$"
              style="min-width: 150px;"
              class="unit-price-input"
            ></v-text-field>
          </template>

          <template v-slot:item.average_cost="{ item }">
            <div class="text-end">
              <strong>{{ formatCurrency(item.average_cost || 0) }}</strong>
              <div class="text-caption text-grey">Calculado del período</div>
            </div>
          </template>

          <template v-slot:item.annual_demand="{ item }">
            <div class="text-end">
              <strong>{{ item.annual_demand || 0 }}</strong>
              <div class="text-caption text-grey">Vendido en período</div>
            </div>
          </template>

          <template v-slot:item.total_value="{ item }">
            <strong>{{ formatCurrency((item.average_cost || 0) * (item.annual_demand || 0)) }}</strong>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="primary"
              @click="editProduct(item)"
            ></v-btn>
            <v-btn
              icon="mdi-chart-line"
              size="small"
              variant="text"
              color="info"
              @click="viewAnalytics(item)"
            ></v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon icon="mdi-alert" color="warning" size="large"></v-icon>
              <p class="text-subtitle-1 mt-2">No se encontraron productos</p>
            </div>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>

    <!-- Modal de edición de producto -->
    <v-dialog v-model="showEditDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>Editar Costos - {{ selectedProduct?.name }}</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="saveProduct">
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="editForm.unit_price"
                  label="Precio Unitario"
                  type="text"
                  prefix="$"
                  :rules="[v => (!v || /^\d{1,3}(\.\d{3})*(\,\d{0,2})?$/.test(v)) || 'Formato inválido. Use puntos para separar miles y coma para decimales']"
                  @input="formatPriceInput"
                  @blur="parsePrice"
                  hint="Formato: $1.234.567,89 (use punto para miles, coma para decimales)"
                  persistent-hint
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="editForm.average_cost"
                  label="Costo Promedio (Calculado)"
                  type="number"
                  step="0.01"
                  min="0"
                  prefix="$"
                  :rules="[v => v >= 0 || 'El costo debe ser mayor o igual a 0']"
                  readonly
                  hint="Este valor se calcula automáticamente del período"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editForm.annual_demand"
                  label="Demanda del Período (Calculada)"
                  type="number"
                  min="0"
                  :rules="[v => v >= 0 || 'La demanda debe ser mayor o igual a 0']"
                  readonly
                  hint="Este valor se calcula automáticamente del período"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editForm.lead_time_days"
                  label="Tiempo de Entrega (días)"
                  type="number"
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editForm.safety_stock"
                  label="Stock de Seguridad"
                  type="number"
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="editForm.max_stock"
                  label="Stock Máximo"
                  type="number"
                  min="0"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editForm.abc_category"
                  :items="[
                    { title: 'Categoría A (Alto valor)', value: 'A' },
                    { title: 'Categoría B (Valor medio)', value: 'B' },
                    { title: 'Categoría C (Bajo valor)', value: 'C' }
                  ]"
                  label="Categoría ABC"
                  clearable
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-alert
                  type="info"
                  variant="tonal"
                  class="mb-4"
                >
                  <strong>Valor Total Anual:</strong> {{ formatCurrency((editForm.average_cost || 0) * (editForm.annual_demand || 0)) }}
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="showEditDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="saveProduct" :loading="saving">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de análisis del producto -->
    <v-dialog v-model="showAnalyticsDialog" max-width="1000">
      <v-card>
        <v-card-title>Análisis - {{ selectedProduct?.name }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-card variant="outlined">
                <v-card-title>Información Financiera</v-card-title>
                <v-card-text>
                  <div class="d-flex justify-space-between mb-2">
                    <span>Costo Promedio:</span>
                    <strong>{{ formatCurrency(selectedProduct?.average_cost || 0) }}</strong>
                  </div>
                  <div class="d-flex justify-space-between mb-2">
                    <span>Demanda Anual:</span>
                    <strong>{{ selectedProduct?.annual_demand || 0 }} unidades</strong>
                  </div>
                  <div class="d-flex justify-space-between mb-2">
                    <span>Valor Total Anual:</span>
                    <strong>{{ formatCurrency((selectedProduct?.average_cost || 0) * (selectedProduct?.annual_demand || 0)) }}</strong>
                  </div>
                  <div class="d-flex justify-space-between">
                    <span>Categoría ABC:</span>
                    <v-chip
                      v-if="selectedProduct?.abc_category"
                      :color="getABCColor(selectedProduct.abc_category)"
                      size="small"
                    >
                      {{ selectedProduct.abc_category }}
                    </v-chip>
                    <span v-else class="text-grey">Sin categoría</span>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card variant="outlined">
                <v-card-title>Información de Inventario</v-card-title>
                <v-card-text>
                  <div class="d-flex justify-space-between mb-2">
                    <span>Stock Mínimo:</span>
                    <strong>{{ selectedProduct?.min_stock || 0 }}</strong>
                  </div>
                  <div class="d-flex justify-space-between mb-2">
                    <span>Stock Máximo:</span>
                    <strong>{{ selectedProduct?.max_stock || 0 }}</strong>
                  </div>
                  <div class="d-flex justify-space-between">
                    <span>Tiempo de Entrega:</span>
                    <strong>{{ selectedProduct?.lead_time_days || 0 }} días</strong>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" @click="showAnalyticsDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as XLSX from 'xlsx'
import api from '@/api'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

export default {
  name: 'CostManagement',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      loading: false,
      calculating: false,
      calculatingPeriod: false,
      exporting: false,
      saving: false,
      showEditDialog: false,
      showAnalyticsDialog: false,
      selectedProduct: null,
      selectedPeriod: 'this_year',
      periodInfo: null,
      periodSummary: null,
      filters: {
        search: '',
        category: '',
        abcCategory: null,
        status: ''
      },
      periodOptions: [
        { title: 'Últimos 30 días', value: 'last_30_days' },
        { title: 'Últimos 60 días', value: 'last_60_days' },
        { title: 'Últimos 90 días', value: 'last_90_days' },
        { title: 'Este mes', value: 'this_month' },
        { title: 'Este trimestre', value: 'this_quarter' },
        { title: 'Este año', value: 'this_year' }
      ],
      editForm: {
        average_cost: 0,
        annual_demand: 0,
        unit_price: 0,
        unit_price_numeric: 0,
        lead_time_days: 0,
        safety_stock: 0,
        max_stock: 0,
        abc_category: null
      },
      headers: [
        { title: 'SKU', key: 'sku', align: 'start', sortable: true, width: '100px' },
        { title: 'Nombre', key: 'name', align: 'start', sortable: true, width: '200px' },
        { title: 'Categoría', key: 'category', align: 'start', sortable: true, width: '120px' },
        { title: 'Precio Unit.', key: 'unit_price', align: 'end', sortable: true, width: '140px' },
        { title: 'Costo Prom.', key: 'average_cost', align: 'end', sortable: true, width: '120px' },
        { title: 'Demanda del Período', key: 'annual_demand', align: 'end', sortable: true, width: '110px' },
        { title: 'Valor Total', key: 'total_value', align: 'end', sortable: true, width: '120px' },
        { title: 'Categoría ABC', key: 'abc_category', align: 'center', sortable: true, width: '90px' },
        { title: 'Estado', key: 'status', align: 'center', sortable: true, width: '100px' },
        { title: 'Acciones', key: 'actions', align: 'center', sortable: false, width: '80px' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      products: 'products/allProducts',
      categories: 'categories/allCategories'
    }),
    categoryOptions() {
      // Extraer categorías únicas de los productos
      const uniqueCategories = [...new Set(this.products.map(p => p.category).filter(Boolean))]
      return uniqueCategories.map(cat => ({ title: cat, value: cat }))
    },
    filteredProducts() {
      let result = this.products || []
      
      if (this.filters.category) {
        result = result.filter(product => product.category === this.filters.category)
      }
      
      if (this.filters.abcCategory !== null && this.filters.abcCategory !== undefined) {
        if (this.filters.abcCategory === 'null') {
          // Filtrar productos sin categoría ABC
          result = result.filter(product => !product.abc_category)
        } else {
          // Filtrar por categoría ABC específica
          result = result.filter(product => product.abc_category === this.filters.abcCategory)
        }
      }
      
      if (this.filters.status) {
        result = result.filter(product => product.status === this.filters.status)
      }
      
      return result
    }
  },
  methods: {
    ...mapActions({
      fetchProducts: 'products/fetchProducts',
      fetchCategories: 'categories/fetchCategories',
      updateProduct: 'products/updateProduct'
    }),
    formatCurrency(value) {
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value || 0)
    },
    getABCColor(category) {
      switch (category) {
        case 'A': return 'success'
        case 'B': return 'warning'
        case 'C': return 'error'
        default: return 'grey'
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 'active': return 'success'
        case 'discontinued': return 'error'
        case 'low_stock': return 'warning'
        case 'out_of_stock': return 'error'
        default: return 'grey'
      }
    },
    getStatusText(status) {
      switch (status) {
        case 'active': return 'Activo'
        case 'discontinued': return 'Descontinuado'
        case 'low_stock': return 'Bajo Stock'
        case 'out_of_stock': return 'Sin Stock'
        default: return status
      }
    },
    async updateProductCost(product) {
      try {
        // Crear objeto del producto con solo el campo que queremos actualizar
        const productToUpdate = {
          id: product.id,
          average_cost: product.average_cost
        }
        
        await this.updateProduct(productToUpdate)
        this.showMessage('Costo actualizado correctamente', 'success')
      } catch (error) {
        console.error('Error updating cost:', error)
        this.showMessage('Error al actualizar el costo', 'error')
      }
    },
    async updateProductUnitPrice(product) {
      try {
        // Convertir el valor formateado a número
        let numericValue = 0
        if (product.unit_price_display) {
          numericValue = parseFloat(product.unit_price_display.replace(/\./g, '').replace(',', '.'))
          if (isNaN(numericValue)) numericValue = 0
        }
        
        const response = await api.updateProductUnitPrice(product.id, {
          unit_price: numericValue
        })
        
        // Actualizar el valor numérico del producto
        product.unit_price = numericValue
        
        this.showMessage('Precio unitario actualizado correctamente', 'success')
      } catch (error) {
        console.error('Error updating unit price:', error)
        this.showMessage('Error al actualizar el precio unitario', 'error')
      }
    },
    async calculatePeriodStatistics() {
      try {
        this.calculatingPeriod = true
        const response = await api.getCostStatistics(this.selectedPeriod)
        
        // Actualizar productos con datos del período
        const periodProducts = response.data.products || []
        
        // Actualizar productos existentes con datos del período
        this.products.forEach(product => {
          const periodData = periodProducts.find(p => p.id === product.id)
          if (periodData) {
            product.average_cost = periodData.average_price
            product.annual_demand = periodData.period_quantity
            product.unit_price = periodData.unit_price
          }
          // Inicializar el campo de display formateado
          product.unit_price_display = this.formatPriceForInput(product.unit_price || 0)
        })
        
        this.periodInfo = response.data.period
        this.periodSummary = response.data.summary
        
        this.showMessage('Estadísticas del período calculadas correctamente', 'success')
      } catch (error) {
        console.error('Error calculating period statistics:', error)
        this.showMessage('Error al calcular estadísticas del período', 'error')
      } finally {
        this.calculatingPeriod = false
      }
    },
    formatPeriodLabel(period) {
      if (!period) return ''
      
      const startDate = new Date(period.start_date)
      const endDate = new Date(period.end_date)
      
      const formatDate = (date) => {
        return date.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        })
      }
      
      return `${formatDate(startDate)} - ${formatDate(endDate)}`
    },
    editProduct(product) {
      this.selectedProduct = product
      
      // Formatear precio unitario para mostrar
      const unitPrice = product.unit_price || 0
      const formattedPrice = this.formatPriceForInput(unitPrice)
      
      this.editForm = {
        average_cost: product.average_cost || 0,
        annual_demand: product.annual_demand || 0,
        unit_price: formattedPrice,
        unit_price_numeric: unitPrice,
        lead_time_days: product.lead_time_days || 0,
        safety_stock: product.safety_stock || 0,
        max_stock: product.max_stock || 0,
        abc_category: product.abc_category
      }
      this.showEditDialog = true
    },
    async saveProduct() {
      if (!this.$refs.editForm.validate()) return
      
      try {
        this.saving = true
        
        // Crear objeto del producto con los campos actualizados
        const productToUpdate = {
          id: this.selectedProduct.id,
          average_cost: this.editForm.average_cost,
          annual_demand: this.editForm.annual_demand,
          unit_price: this.editForm.unit_price_numeric || 0, // Usar valor numérico
          lead_time_days: this.editForm.lead_time_days,
          safety_stock: this.editForm.safety_stock,
          max_stock: this.editForm.max_stock,
          abc_category: this.editForm.abc_category
        }
        
        await this.updateProduct(productToUpdate)
        this.showEditDialog = false
        this.fetchProducts()
        this.showMessage('Producto actualizado correctamente', 'success')
      } catch (error) {
        console.error('Error saving product:', error)
        this.showMessage('Error al guardar el producto', 'error')
      } finally {
        this.saving = false
      }
    },
    viewAnalytics(product) {
      this.selectedProduct = product
      this.showAnalyticsDialog = true
    },
    async calculateABCCategories() {
      try {
        this.calculating = true
        const response = await api.calculateABCCategories()
        await this.fetchProducts()
        this.showMessage(response.data.message, 'success')
      } catch (error) {
        console.error('Error calculating ABC categories:', error)
        this.showMessage(error.response?.data?.error || 'Error al calcular las categorías ABC', 'error')
      } finally {
        this.calculating = false
      }
    },
    async exportCosts() {
      try {
        this.exporting = true
        
        const data = this.filteredProducts.map(product => ({
          SKU: product.sku,
          Nombre: product.name,
          Categoría: product.category,
          'Precio Unitario': product.unit_price || 0,
          'Costo Promedio': product.average_cost || 0,
          'Demanda Anual': product.annual_demand || 0,
          'Valor Total': (product.average_cost || 0) * (product.annual_demand || 0),
          'Categoría ABC': product.abc_category || 'Sin categoría',
          Estado: this.getStatusText(product.status),
          'Stock Mínimo': product.min_stock,
          'Stock Máximo': product.max_stock || 0,
          'Tiempo de Entrega': product.lead_time_days || 0
        }))
        
        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Costos y Precios')
        
        const fileName = `costos_productos_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(workbook, fileName)
        
        this.showMessage('Datos exportados correctamente', 'success')
      } catch (error) {
        console.error('Error exporting data:', error)
        this.showMessage('Error al exportar los datos', 'error')
      } finally {
        this.exporting = false
      }
    },
    showMessage(message, type) {
      // Simple notification system
      if (type === 'success') {
        this.$store.dispatch('showSuccess', message)
      } else if (type === 'error') {
        this.$store.dispatch('showError', message)
      }
    },
    formatPriceInput(event) {
      // Obtener el valor sin formato
      let value = event.target.value.replace(/[^\d,]/g, '')
      
      if (!value) return
      
      // Separar parte entera y decimal
      const parts = value.split(',')
      let integerPart = parts[0]
      const decimalPart = parts[1] || ''
      
      // Formatear parte entera con puntos cada 3 dígitos
      if (integerPart.length > 3) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
      
      // Recomponer el valor
      let formattedValue = integerPart
      if (parts.length > 1) {
        formattedValue += ',' + decimalPart.substring(0, 2) // Máximo 2 decimales
      }
      
      // Actualizar el campo
      this.editForm.unit_price = formattedValue
    },
    parsePrice(event) {
      // Convertir el valor formateado a número para almacenamiento
      const formattedValue = event.target.value
      if (!formattedValue) {
        this.editForm.unit_price_numeric = 0
        return
      }
      
      // Remover puntos y reemplazar coma por punto para conversión a número
      const numericValue = parseFloat(formattedValue.replace(/\./g, '').replace(',', '.'))
      this.editForm.unit_price_numeric = isNaN(numericValue) ? 0 : numericValue
    },
    formatPriceForInput(value) {
      // Formatear el valor para mostrar en el input
      if (!value || value === 0) return '0'
      
      let formattedValue = value.toString()
      
      // Separar parte entera y decimal
      const parts = formattedValue.split('.')
      let integerPart = parts[0]
      const decimalPart = parts[1] || ''
      
      // Formatear parte entera con puntos cada 3 dígitos
      if (integerPart.length > 3) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
      
      // Recomponer el valor usando coma para decimales (formato chileno)
      formattedValue = integerPart
      if (decimalPart) {
        formattedValue += ',' + decimalPart.substring(0, 2) // Máximo 2 decimales
      }
      
      return formattedValue
    },
    formatTablePriceInput(item, event) {
      // Obtener el valor sin formato
      let value = event.target.value.replace(/[^\d,]/g, '')
      
      if (!value) return
      
      // Separar parte entera y decimal
      const parts = value.split(',')
      let integerPart = parts[0]
      const decimalPart = parts[1] || ''
      
      // Formatear parte entera con puntos cada 3 dígitos
      if (integerPart.length > 3) {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
      
      // Recomponer el valor
      let formattedValue = integerPart
      if (parts.length > 1) {
        formattedValue += ',' + decimalPart.substring(0, 2) // Máximo 2 decimales
      }
      
      // Actualizar el campo
      item.unit_price_display = formattedValue
    },
    initializePriceDisplayFields() {
      this.products.forEach(product => {
        product.unit_price_display = this.formatPriceForInput(product.unit_price || 0)
      })
    }
  },
  async mounted() {
    this.loading = true
    try {
      await Promise.all([
        this.fetchProducts(),
        this.fetchCategories().catch(() => {
          // Ignore categories error for now
          console.log('Categories not available')
        })
      ])
      
      // Inicializar campos de display para precios
      this.initializePriceDisplayFields()
      
      // Cargar estadísticas del período por defecto
      await this.calculatePeriodStatistics()
    } catch (error) {
      console.error('Error loading data:', error)
      this.showMessage('Error al cargar los datos', 'error')
    } finally {
      this.loading = false
    }
  }
}
</script>

<style scoped>
.cost-management-container {
  padding: 20px;
}

.v-card {
  border-radius: 8px;
}

.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-data-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.v-data-table .v-data-table__thead {
  background-color: #f8f9fa !important;
}

.v-data-table .v-data-table__thead .v-data-table__th {
  background-color: #f8f9fa !important;
  font-weight: 600 !important;
  color: #495057 !important;
}

.v-data-table .v-data-table__tbody tr:hover {
  background-color: #f8f9fa !important;
}

/* Estilos para el input de precio unitario en la tabla */
.unit-price-input {
  min-width: 150px !important;
}

.unit-price-input .v-field__input {
  padding: 8px 12px !important;
  font-size: 14px !important;
  text-align: right !important;
}

.unit-price-input .v-field__prepend-inner {
  padding-right: 4px !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .cost-management-container {
    padding: 10px;
  }
  
  .cost-management-title {
    font-size: 1.1rem !important;
    margin-bottom: 8px;
  }
  

  
  /* Filtros responsivos */
  .cost-management-filters {
    margin-bottom: 16px !important;
  }
  
  .cost-management-filters .v-col {
    padding: 4px 8px !important;
  }
  
  .v-text-field, .v-select {
    margin-bottom: 8px;
  }
  
  .v-text-field .v-field__input,
  .v-select .v-field__input {
    font-size: 14px !important;
    padding: 8px 12px !important;
  }
  
  /* Cards para resumen de período */
  .v-card-title {
    font-size: 1rem !important;
    padding: 12px 16px 8px !important;
  }
  
  .v-card-text {
    padding: 8px 16px 16px !important;
  }
  
  /* Optimización de espacios */
  .v-row {
    margin: 0 -8px !important;
  }
  
  .v-col {
    padding: 0 8px !important;
  }
  
  /* Modal responsivo */
  .v-dialog .v-card {
    margin: 16px !important;
    width: calc(100% - 32px) !important;
    max-width: none !important;
  }
  
  .v-dialog .v-card-title {
    font-size: 1.1rem !important;
    padding: 16px !important;
  }
  
  .v-dialog .v-card-text {
    padding: 0 16px 16px !important;
  }
}

@media (max-width: 480px) {
  .cost-management-container {
    padding: 8px;
  }
  
  .cost-management-title {
    font-size: 1rem !important;
  }
  
  /* Botones en móvil mantienen tamaño estándar */
  
  /* Resumen de período en móvil */
  .text-h5 {
    font-size: 1.2rem !important;
  }
  
  .text-caption {
    font-size: 0.75rem !important;
  }
}
</style> 